// Design approval choice. A free decision, NOT a paid add-on:
//
//   Approve before stitching (+$15, Recommended)
//     We email a digital proof; you OK it before we put it on the machines.
//
//   Skip, free
//     Straight to embroidery, fastest turnaround.
//
//   Phase 22b: any Template-attached upsell with appliesTo="order" AND
//   replacesStep="approval" renders as an additional tile. Picking one
//   replaces the standard approve/skip choice with a premium variant
//   (e.g. "Alternate colourway proof — proof with two colour options").
//   The upsell's price applies in lieu of the standard approval fee.
//
// Wave 3: the accordion step wrapper was retired; these controls now
// render directly inside the consolidated "Finish your order" screen
// (see sections/FinishSection.tsx). App owns the choice state and passes
// the handlers, so the emitted cart properties are unchanged:
//   - the visible "Design approval" line (skip / approve / addon label)
//     is built in pricing.ts from approvalChoice + approvalAddonId, both
//     set by onChoose / onChooseAddon here.

import type { ApprovalChoice } from "../pricing";
import { approvalFeeFor } from "../pricing";
import type { Config } from "../types";

export function ApprovalControls({
  choice,
  onChoose,
  config,
  addonId,
  onChooseAddon,
}: {
  choice: ApprovalChoice;
  onChoose: (c: Exclude<ApprovalChoice, null>) => void;
  config: Config;
  /** Currently picked approval-replacing upsell id (Phase 22b). */
  addonId: string | null;
  /** Customer picked a replacement upsell. The App will set
   *  approvalChoice="approve" + addonId in lockstep. */
  onChooseAddon: (upsellId: string) => void;
}) {
  const approvalFee = approvalFeeFor(config);
  const approvalFeeDollars = approvalFee / 100;

  const approvalReplacers = (config.upsells ?? []).filter(
    (u) => u.appliesTo === "order" && u.replacesStep === "approval",
  );
  const activeAddon = addonId
    ? approvalReplacers.find((u) => u.id === addonId) ?? null
    : null;

  return (
    <div className="rk-pc-sec__body">
      <div className="rk-pc-mode-row">
        <button
          type="button"
          className={`rk-pc-mode-tile${choice === "approve" && !activeAddon ? " is-selected" : ""}`}
          onClick={() => onChoose("approve")}
        >
          <span className="rk-pc-mode-tile__badge">Recommended</span>
          <span className="rk-pc-mode-tile__title">Approve before stitching</span>
          <span className="rk-pc-mode-tile__desc">
            We email a digital proof. You OK it before we put it on the machines.
          </span>
          <span className="rk-pc-mode-tile__price">+${approvalFeeDollars}</span>
        </button>
        <button
          type="button"
          className={`rk-pc-mode-tile${choice === "skip" ? " is-selected" : ""}`}
          onClick={() => onChoose("skip")}
        >
          <span className="rk-pc-mode-tile__title">Skip, go straight in</span>
          <span className="rk-pc-mode-tile__desc">
            Fastest turnaround. Our digitisers prepare every design by hand
            and get straight to stitching.
          </span>
          <span className="rk-pc-mode-tile__price is-free">Free</span>
        </button>
      </div>

      {/* Phase 22b: approval-step replacements. Tinted with the upsell's
          accent colour when one is provided. */}
      {approvalReplacers.length > 0 && (
        <div className="rk-pc-addon-row">
          <span className="rk-pc-addon-row__head">Or upgrade your approval</span>
          <div className="rk-pc-mode-row">
            {approvalReplacers.map((u) => {
              const selected = activeAddon?.id === u.id;
              const accent = u.accent ?? undefined;
              return (
                <button
                  key={u.id}
                  type="button"
                  className={`rk-pc-mode-tile rk-pc-mode-tile--upsell${selected ? " is-selected" : ""}`}
                  style={
                    accent
                      ? ({ ["--rk-pc-upsell-accent" as any]: accent } as React.CSSProperties)
                      : undefined
                  }
                  onClick={() => onChooseAddon(u.id)}
                >
                  <span className="rk-pc-mode-tile__title">{u.label}</span>
                  <span className="rk-pc-mode-tile__desc">{u.hint ?? ""}</span>
                  <span className="rk-pc-mode-tile__price">
                    +${(u.priceCents / 100).toFixed(0)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
